<template>
  <div class="rd-screen">
    <div v-if="statusCounts.length" class="rd-tools rd-tools--filters">
      <div class="rd-chips" role="group" :aria-label="$t('status')">
        <button type="button" class="rd-chip" :aria-pressed="String(!status)" @click="$emit('update:status', '')">
          {{ $t("all") }}
          <b class="rd-num">{{ total }}</b>
        </button>
        <button
          v-for="item in statusCounts"
          :key="item.state"
          type="button"
          class="rd-chip rd-chip--status"
          :aria-pressed="String(status === item.state)"
          :style="chipStyle(item.state, status === item.state)"
          @click="$emit('update:status', status === item.state ? '' : item.state)"
        >
          <i class="rd-dot" :style="dotStyle(item.state)"></i>
          {{ $t(item.state) }}
          <b class="rd-num">{{ item.count }}</b>
        </button>
      </div>
      <span class="rd-tools__spacer"></span>
      <label v-if="deliveryCounts.length > 1" class="rd-view">
        <span>{{ $t("delivery_location") }}</span>
        <select
          class="rd-select rd-select--sm"
          :value="delivery === null ? '' : String(delivery)"
          :aria-label="$t('delivery_location')"
          @change="$emit('update:delivery', $event.target.value === '' ? null : Number($event.target.value))"
        >
          <option value="">{{ $t("all") }}</option>
          <option v-for="type in deliveryCounts" :key="type.id" :value="String(type.id)">
            {{ $t(type.key) }} ({{ type.count }})
          </option>
        </select>
      </label>
    </div>

    <report-tools :copy="rowCount > 0" @copy="copyTable">
      <label class="rd-live" :class="{ 'is-on': live }">
        <span class="rd-live__dot"></span>
        <input
          type="checkbox"
          class="rd-switch"
          role="switch"
          :checked="live"
          :aria-label="$t('report_desktop.live')"
          @change="$emit('update:live', $event.target.checked)"
        />
        <span class="rd-num">{{ liveText }}</span>
      </label>
    </report-tools>

    <report-state v-if="failed && !hasOrders" kind="error" @retry="$emit('retry')"></report-state>
    <report-state v-else-if="loading && !hasOrders" kind="loading"></report-state>
    <report-state
      v-else-if="!rowCount"
      icon="mdi-receipt-text-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl rd-ord">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">{{ $t("r_b") }} {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" class="rd-ord__c-time" :aria-sort="ariaSort('time')">
              <button type="button" @click="sortBy('time')">{{ $t("time") }} {{ sortArrow("time") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("name_surname_phone") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col" class="rd-ord__c-address">{{ $t("address") }}</th>
            <th scope="col">{{ $t("ordered") }}</th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('amount')">
              <button type="button" @click="sortBy('amount')">{{ $t("total") }} {{ sortArrow("amount") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('statusRank')">
              <button type="button" @click="sortBy('statusRank')">{{ $t("status") }} {{ sortArrow("statusRank") }}</button>
            </th>
          </tr>
        </thead>
        <tbody v-for="day in dayBlocks" :key="day.date">
          <tr v-if="multiDay" class="rd-tbl__group">
            <td colspan="7">
              {{ dayTitle(day.date) }}
              <span class="rd-section__sub">{{ day.summary }}</span>
            </td>
          </tr>
          <tr
            v-for="row in day.rows"
            :key="row.key"
            data-open
            :class="{ 'is-selected': row.order.id === activeId }"
            @click="open(row, $event)"
          >
            <td class="rd-num">
              <button type="button" class="rd-link-btn rd-ord__id" :aria-label="$t('order_details')" @click.stop="open(row, $event)">
                #{{ row.order.id }}
              </button>
              <span class="rd-ord__time-inline rd-muted">{{ row.time }}</span>
            </td>
            <td class="rd-ord__c-time">
              <b class="rd-num">{{ row.time || "–" }}</b>
              <span v-if="row.asap" class="rd-tag rd-tag--accent">{{ $t("asap") }}</span>
            </td>
            <td class="rd-ord__guest">
              <span class="rd-ord__name">{{ row.displayName }}</span>
              <span class="rd-muted rd-num">{{ row.guest.phone }}</span>
              <button
                v-if="isAdmin && row.order.restaurant"
                type="button"
                class="rd-link-btn rd-ord__restaurant"
                @click.stop="showRestaurant(row.order.restaurant)"
              >
                <v-icon size="14" color="#c2410c">mdi-store</v-icon>
                {{ row.order.restaurant.name }}
              </button>
              <span class="rd-ord__addr-inline">
                <v-icon size="14">{{ row.delivery.icon }}</v-icon>
                {{ row.delivery.label }}<template v-if="row.address">, {{ row.address }}</template>
              </span>
            </td>
            <td class="rd-ord__address rd-ord__c-address">
              <span class="rd-ord__delivery">
                <v-icon size="15">{{ row.delivery.icon }}</v-icon>
                {{ row.delivery.label }}
              </span>
              <span v-if="row.address" class="rd-muted">{{ row.address }}</span>
            </td>
            <td>
              <ul class="rd-lines">
                <li v-for="(line, index) in row.lines.slice(0, MAX_LINES)" :key="index">
                  <b class="rd-num">{{ line.quantity }}×</b>
                  <span>{{ line.name }}</span>
                </li>
                <li v-if="row.lines.length > MAX_LINES" class="rd-muted">
                  {{ $t("guest_desktop.more_items", { n: row.lines.length - MAX_LINES }) }}
                </li>
              </ul>
            </td>
            <td class="is-right rd-num">
              <b>{{ row.amountText }}</b>
            </td>
            <td @click.stop>
              <v-menu offset-y left content-class="rd-vmenu">
                <template #activator="{ on, attrs }">
                  <button
                    type="button"
                    class="rd-sbadge"
                    :class="{ 'is-busy': changingId === row.order.id }"
                    :style="badgeStyle(row.state)"
                    :aria-label="`${$t('status')}: ${$t(row.state)}`"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <i class="rd-dot" :style="dotStyle(row.state)"></i>
                    {{ $t(row.state) }}
                    <v-icon size="14">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <ul class="rd-vmenu__list" role="menu">
                  <li v-for="item in orderStatuses" :key="item.value">
                    <button
                      type="button"
                      role="menuitem"
                      :disabled="item.value === row.state"
                      @click="requestStatusChange(row.order, row.guest, item.value)"
                    >
                      <v-icon size="16" :style="{ color: getStatusColors(item.value).fg }">{{ item.icon }}</v-icon>
                      {{ $t(item.value) }}
                    </button>
                  </li>
                </ul>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <prep-time-dialog v-model="prepTimeDialogOpen" @select-minutes="confirmPrepMinutes" @ready-now="confirmReadyNow"></prep-time-dialog>
    <restaurant-details-dialog v-model="restaurantDialog" :restaurant="selectedRestaurant"></restaurant-details-dialog>
  </div>
</template>

<script>
import moment from "moment";
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import PrepTimeDialog from "@/components/guest/PrepTimeDialog.vue";
import RestaurantDetailsDialog from "@/components/dialogs/RestaurantDetailsDialog.vue";
import { orderTotal } from "@/utils/guestOrderTotal";
import { DELIVERY_TYPES, menuState } from "@/utils/guestOrderStatus";

const MAX_LINES = 3;

/**
 * Narudžbe kupaca (prikaz): filteri po statusu, tabela po danima, meni statusa u redu.
 * Ne učitava ništa: podatke i radnje daje ekran koji ga koristi (GuestDesktopOrders).
 * Promena statusa ide događajem "change-status" ({ order, guest, newStatus }).
 */
export default {
  name: "GuestOrdersView",
  mixins: [hasGuestOrderStatus, hasTableSort],
  components: { ReportState, ReportTools, PrepTimeDialog, RestaurantDetailsDialog },
  props: {
    /** Datum → redovi (jedna narudžba po redu) */
    days: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    failed: { type: Boolean, default: false },
    /** Ima li ikakvih narudžbi u periodu (pre filtera) */
    hasOrders: { type: Boolean, default: false },
    statusCounts: { type: Array, default: () => [] },
    /** Broj narudžbi posle pretrage i vrste isporuke (za "Sve") */
    total: { type: Number, default: 0 },
    status: { type: String, default: "" },
    delivery: { type: Number, default: null },
    deliveryCounts: { type: Array, default: () => [] },
    changingId: { type: [Number, String], default: null },
    activeId: { type: Number, default: null },
    isAdmin: { type: Boolean, default: false },
    search: { type: String, default: "" },
    live: { type: Boolean, default: false },
    liveText: { type: String, default: "" },
  },
  data: () => ({
    MAX_LINES,
    sort: { key: "", dir: 1 },
    restaurantDialog: false,
    selectedRestaurant: null,
  }),
  computed: {
    multiDay() {
      return Object.keys(this.days).length > 1;
    },
    dayBlocks() {
      return Object.entries(this.days).map(([date, guests]) => {
        const rows = this.sortRows(guests.map((guest) => this.toRow(guest)));
        const amount = rows.reduce((sum, row) => sum + row.amount, 0);
        return {
          date,
          rows,
          summary: `${rows.length} · ${amount.toFixed(2)} ${this.dayCurrency(rows)}`,
        };
      });
    },
    rowCount() {
      return this.dayBlocks.reduce((sum, day) => sum + day.rows.length, 0);
    },
    emptyTitle() {
      if (this.search) {
        return this.$t("report_mobile.no_results_for", { search: this.search });
      }
      return this.hasOrders ? this.$t("guest_desktop.no_match") : this.$t("no_orders_found");
    },
    emptyText() {
      return this.search ? "" : this.hasOrders ? this.$t("guest_desktop.no_match_hint") : this.$t("report_desktop.empty_hint");
    },
  },
  methods: {
    /** Prvi klik na vreme, broj i ime ide rastuće, na iznos opadajuće. */
    sortBy(key) {
      const first = key === "amount" ? -1 : 1;
      this.sort = { key, dir: this.sort.key === key ? -this.sort.dir : first };
    },
    toRow(guest) {
      const order = guest.orders_guest[0];
      const state = menuState(order.state);
      const amount = orderTotal(order);
      return {
        key: String(order.id),
        guest,
        order,
        state,
        id: Number(order.id) || 0,
        time: this.formatOrderTime(order),
        asap: this.isAsapOrder(order),
        displayName: `${this.toLatin(guest.name || "")} ${this.toLatin(guest.lastname || "")}`.trim(),
        name: `${guest.name || ""} ${guest.lastname || ""}`.trim().toLowerCase(),
        amount,
        amountText: `${amount.toFixed(2)} ${this.currencyOf(order)}`,
        statusRank: Math.max(0, this.orderStatuses.findIndex((item) => item.value === state)),
        delivery: this.deliveryOf(order),
        address: order.delivery_type === 0 ? order.location?.address || "" : "",
        lines: (order.order_guest_transactions || []).map((transaction) => ({
          quantity: transaction.quantity,
          name: this.toLatin(transaction.food?.name || transaction.meals?.name || ""),
        })),
      };
    },
    currencyOf(order) {
      return order?.restaurant?.settings?.price || this.restaurantCurrency;
    },
    dayCurrency(rows) {
      const currencies = [...new Set(rows.map((row) => this.currencyOf(row.order)))];
      return currencies.length === 1 ? currencies[0] : currencies.join("/");
    },
    deliveryOf(order) {
      const type = DELIVERY_TYPES.find((item) => item.id === order.delivery_type);
      return type ? { icon: type.icon, label: this.$t(type.key) } : { icon: "mdi-help-circle-outline", label: "" };
    },
    /** Filter statusa: obojen tek kad je izabran, inače bela kartica sa tačkom u boji statusa. */
    chipStyle(state, selected) {
      if (!selected) {
        return {};
      }
      const colors = this.getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg, borderColor: colors.fg };
    },
    /** Značka statusa u redu: uvek u boji statusa. */
    badgeStyle(state) {
      const colors = this.getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg };
    },
    dotStyle(state) {
      return { backgroundColor: this.getStatusColors(state).fg };
    },
    dayTitle(date) {
      const day = moment(date, "YYYY-MM-DD");
      const weekday = day.toDate().toLocaleDateString(this.$i18n.locale === "en" ? "en" : "sr-Latn", { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day.format("DD.MM.YYYY.")}`;
    },
    open(row, event) {
      this.$emit("open", { order: row.order, guest: row.guest, event });
    },
    showRestaurant(restaurant) {
      this.selectedRestaurant = restaurant;
      this.restaurantDialog = true;
    },
    async copyTable() {
      const headers = [
        this.$t("r_b"),
        ...(this.multiDay ? [this.$t("date")] : []),
        this.$t("time"),
        this.$t("name_surname_phone"),
        this.$t("address"),
        this.$t("ordered"),
        this.$t("total"),
        this.$t("status"),
      ];
      const rows = this.dayBlocks.flatMap((day) =>
        day.rows.map((row) => [
          row.order.id,
          ...(this.multiDay ? [day.date] : []),
          row.time,
          `${row.displayName} ${row.guest.phone || ""}`.trim(),
          [row.delivery.label, row.address].filter(Boolean).join(" "),
          row.lines.map((line) => `${line.quantity}x ${line.name}`).join(", "),
          row.amountText,
          this.$t(row.state),
        ])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
